import { Mail } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import type { MassMigrationBatchOrgDto, MassMigrationBatchSummaryDto } from '@/types/massMigration';

interface InviteCountProps {
  org: MassMigrationBatchOrgDto;
}

/**
 * "{sent} of {total}" for one org's welcome invites. The total is only known once the
 * org's Phase 2 (user collection + onboarding) has finished, so until then this shows
 * "Waiting" rather than a misleading "0 of 0". "0 of 0" means Phase 2 ran and found
 * nobody to invite (e.g. the only user was the org owner, who is not invited this way).
 */
export function InviteCount({ org }: InviteCountProps) {
  const phase = org.userMigrationPhaseStatus;
  if (org.status === 'InProgress' || phase === 'Pending' || phase === 'InProgress') {
    return <span className="text-muted-foreground">Waiting</span>;
  }
  if (phase === 'NotApplicable') {
    return <span className="text-muted-foreground">—</span>;
  }

  const { invitesSent: sent, invitesTotal: total } = org;
  const done = total > 0 && sent === total;
  return (
    <span className={done ? 'text-success' : total > 0 ? 'text-foreground' : 'text-muted-foreground'}>
      {sent} of {total}
    </span>
  );
}

interface InviteDrainRowProps {
  batch: MassMigrationBatchSummaryDto;
}

/**
 * Welcome-invite progress for a batch. Batch children defer invites to a paced drain
 * (up to 50/min across all batches) that starts once every organization's Phase 1 is
 * done, so this row is the only place the operator can see that work moving.
 */
export function InviteDrainRow({ batch }: InviteDrainRowProps) {
  const total = batch.invitesPending + batch.invitesSent + batch.invitesFailed;
  if (total === 0) return null;

  const done = batch.invitesSent + batch.invitesFailed;
  const pct = Math.round((done / total) * 100);
  const phase1Running = batch.status === 'InProgress' || batch.status === 'Pending';

  let detail: string;
  if (batch.invitesPending === 0) {
    detail = 'All invitations sent';
  } else if (phase1Running) {
    detail = 'Queued — starts when all organizations finish';
  } else {
    detail = `${batch.invitesPending} queued · up to 50 per minute`;
  }

  return (
    <div className="rounded-lg border bg-card p-4 space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <span className="text-sm font-medium">Emailed invites</span>
          <span className="text-sm text-muted-foreground">
            {batch.invitesSent} of {total} sent
          </span>
          {batch.invitesFailed > 0 && (
            <span className="text-sm text-warning">{batch.invitesFailed} could not be sent</span>
          )}
        </div>
        <span className="text-xs text-muted-foreground">{detail}</span>
      </div>
      <Progress value={pct} className="h-2" aria-label="Emailed invite progress" />
    </div>
  );
}
